import { define } from './define';

// Keys start with "account.". Spanish first; English must have the same keys (TypeScript checks it).
// Signing in, the second factor, and the person's own page: profile, language, authenticator and notifications.
export const account = define({
  es: {
    'account.title': 'Tu cuenta',

    'account.invitations.title': { one: 'Te han invitado a una marca', other: 'Te han invitado a {count} marcas' },
    'account.invitations.hint': 'No cambia nada hasta que respondas. Si aceptas, entrarás en la marca con el rol que te ofrecen.',
    'account.invitations.as': 'como {role}',
    'account.invitations.by': 'te invita {who}',
    'account.invitations.expires': 'caduca el {date}',
    'account.invitations.accept': 'Aceptar',
    'account.invitations.decline': 'Rechazar',
    'account.invitations.accepted': 'Ya estás en {brand}',
    'account.invitations.declined': 'Invitación rechazada',
    'account.invitations.unknown': 'Si no sabes quién te invita, recházala.',

    'account.profile.title': 'Perfil',
    'account.profile.brands': { one: 'Tu marca', other: 'Tus {count} marcas' },
    'account.profile.paused': 'En pausa',
    'account.profile.nameHint': 'Tu nombre y tu correo los pone un administrador al añadirte a una marca.',
    'account.profile.signOut': 'Cerrar sesión',

    'account.language.title': 'Idioma',
    'account.language.hint': 'El de la interfaz, solo para ti. No cambia el idioma de lo que publica la marca.',
    'account.language.note': 'Se guarda en este navegador.',

    'account.twofa.title': 'App de autenticación',
    'account.twofa.hint': 'Un código de una app del móvil, además del enlace por correo, para entrar.',
    'account.twofa.on': 'Activada',
    'account.twofa.off': 'Sin activar',
    'account.twofa.required': 'Tu rol necesita un segundo factor, así que no se puede quitar. Si pierdes el móvil, un administrador te lo puede restablecer.',
    'account.twofa.intro': 'Añade un código de una app del móvil al entrar. Los administradores y aprobadores lo necesitan; para los demás es opcional.',
    'account.twofa.introShort': 'Los administradores y aprobadores lo necesitan; para los demás es opcional.',
    'account.twofa.setUp': 'App de autenticación configurada',
    'account.twofa.codesLeft': { one: 'Te queda {count} código de recuperación.', other: 'Te quedan {count} códigos de recuperación.' },
    'account.twofa.codesLow': 'Crea otros antes de que se acaben.',
    'account.twofa.newCodes': 'Crear códigos de recuperación nuevos',
    'account.twofa.newCodesHint': 'Los anteriores dejan de valer.',
    'account.twofa.turnOff': 'Quitar',
    'account.twofa.turnOffHint': 'Al entrar ya no se te pedirá ningún código.',
    'account.twofa.removed': 'App de autenticación quitada',

    'account.code.label': 'Código de tu app',

    'account.recovery.title': 'Guarda estos códigos de recuperación',
    'account.recovery.hint': 'Si pierdes el móvil, cada uno te deja entrar una vez. Se muestran ahora y nunca más.',
    'account.recovery.copyAll': 'Copiar todos',
    'account.recovery.saved': 'Los he guardado en un sitio seguro',
    'account.recovery.done': 'Ya los he guardado',
    'account.recovery.fileTitle': 'Códigos de recuperación de Postbay',
    'account.recovery.fileNote': 'Cada uno vale una vez.',
    'account.recovery.fileName': 'codigos-de-recuperacion-postbay.txt',

    'account.enroll.intro': 'Una app de autenticación (Google Authenticator, Microsoft Authenticator, 1Password, Authy…) te da un código de seis cifras nuevo cada 30 segundos.',
    'account.enroll.start': 'Configurar una app de autenticación',
    'account.enroll.step1': 'En tu app de autenticación, añade una cuenta, elige «introducir una clave de configuración» y escribe esta clave (los espacios dan igual):',
    'account.enroll.copyKey': 'Copiar clave',
    'account.enroll.openApp': 'Abrir en una app de este dispositivo',
    'account.enroll.step2': 'Escribe el código de seis cifras que muestra la app ahora.',
    'account.enroll.codeLabel': 'Código de la app',
    'account.enroll.check': 'Comprobar el código',
    'account.enroll.continue': 'Continuar',

    'account.verify.codeLabel': 'Código de tu app de autenticación',
    'account.verify.recoveryLabel': 'Código de recuperación',
    'account.verify.recoveryHint': 'Uno de los diez que te dimos. Cada uno vale una vez.',
    'account.verify.continue': 'Continuar',
    'account.verify.useApp': 'Usar la app',
    'account.verify.useRecovery': 'He perdido el móvil: usar un código de recuperación',

    'account.second.verifyTitle': 'Un paso más',
    'account.second.verifyHint': 'Escribe el código que muestra ahora tu app de autenticación.',
    'account.second.enrollTitle': 'Configura tu segundo factor',
    'account.second.enrollHint': 'Tu rol lo necesita: así, un enlace de acceso robado no basta para aprobar o publicar.',
    'account.second.notYou': '¿No eres tú?',
    'account.second.signOut': 'Cerrar sesión',

    'account.notify.title': 'Avisos',
    'account.notify.hint': 'La campana siempre lo muestra todo. Elige qué te llega también por correo, y por notificación a este y a tus otros navegadores.',
    'account.notify.when': 'Avísame cuando…',
    'account.notify.email': 'Correo',
    'account.notify.push': 'Push',
    'account.notify.emailFor': 'Correo: {what}',
    'account.notify.pushFor': 'Push: {what}',

    'account.push.here': 'Notificaciones en este navegador',
    'account.push.on': 'Activadas',
    'account.push.off': 'Desactivadas',
    'account.push.unsupportedChip': 'No disponibles',
    'account.push.onHint': 'Te llegan aunque la pestaña esté cerrada.',
    'account.push.offHint': 'Actívalas para enterarte sin tener Postbay abierto.',
    'account.push.unsupported': 'Este navegador no puede recibir notificaciones.',
    'account.push.devices': { one: 'Las tienes activadas en {count} navegador.', other: 'Las tienes activadas en {count} navegadores.' },
    'account.push.turnOn': 'Activar aquí',
    'account.push.turnOff': 'Desactivar aquí',
    'account.push.test': 'Enviar una prueba',
    'account.push.onToast': 'Notificaciones activadas en este navegador',
    'account.push.offToast': 'Notificaciones desactivadas en este navegador',
    'account.push.sentTo': { one: 'Enviada a {count} navegador', other: 'Enviada a {count} navegadores' },
    'account.push.notTaken': 'El servicio de notificaciones no la ha aceptado: prueba otra vez en un momento',
    'account.push.noBrowser': 'Ningún navegador tiene las notificaciones activadas',
    'account.push.blocked': 'Las notificaciones están bloqueadas para este sitio: permítelas en los ajustes del sitio del navegador y vuelve a intentarlo',

    // What each notification is about, phrased to follow "Avísame cuando…" (also used by Slack's "Publicar cuando…").
    'account.kind.version.uploaded': 'Hay una versión nueva para revisar',
    'account.kind.comment.created': 'Alguien comenta una pieza',
    'account.kind.version.changes_requested': 'Se piden cambios en una versión',
    'account.kind.version.approved': 'Se aprueba una versión',
    'account.kind.publication.due': 'Toca una publicación que se hace a mano',
    'account.kind.publication.reapproval': 'Un cambio en algo programado necesita confirmación',
    'account.kind.publication.on_hold': 'Se quedan en espera publicaciones programadas',
    'account.kind.publication.published': 'Sale una publicación',
    'account.kind.publication.failed': 'Falla una publicación',
    'account.kind.publication.private': 'Algo se sube pero queda privado',
    'account.kind.account.reconnect': 'Hay que volver a conectar una cuenta',
    'account.kind.account.expiring': 'Está a punto de caducar la conexión de una cuenta',
    'account.kind.webhook.failing': 'Un webhook está fallando',
    'account.kind.slack.failing': 'Slack deja de aceptar mensajes',
    'account.kind.agent.needs_person': 'El agente devuelve una pieza a una persona',
    'account.kind.agent.failed': 'Falla una ejecución del agente',

    'account.login.title': 'Entra en Postbay',
    'account.login.subtitle': 'Te mandamos un enlace por correo: no hay contraseña que recordar.',
    'account.login.subtitleNoEmail': 'Entra con la cuenta de tu equipo.',
    'account.login.email': 'Correo electrónico',
    'account.login.emailPlaceholder': 'tu@empresa.com',
    'account.login.send': 'Mándame un enlace',
    'account.login.sending': 'Enviando…',
    'account.login.sso': 'Entrar con {provider}',
    'account.login.or': 'o',
    'account.login.dev': 'Entrar en desarrollo (sin correo)',
    'account.login.devHint': 'Solo en este servidor de desarrollo: entra al momento con el correo escrito.',
    'account.login.noAccess': '¿Aún no tienes acceso? Pide a un administrador de tu marca que te añada.',
    'account.login.sentTitle': 'Mira tu correo',
    'account.login.sent': 'Si {email} es de alguien de una marca, le llega un enlace para entrar. Vale una vez y caduca en 15 minutos.',
    'account.login.otherEmail': 'Usar otro correo',

    'account.sso.cancelled': 'Se ha cancelado el inicio de sesión.',
    'account.sso.state': 'Ese inicio de sesión no empezó en este navegador o tardó demasiado. Vuelve a empezar.',
    'account.sso.provider': 'No se ha podido contactar con el servicio de inicio de sesión, o no está bien configurado. Inténtalo otra vez o pregunta a un administrador.',
    'account.sso.token': 'El servicio de inicio de sesión ha dado una respuesta que la app no puede aceptar. Inténtalo otra vez o pregunta a un administrador.',
    'account.sso.claims': 'El servicio de inicio de sesión ha dado una respuesta que la app no puede aceptar. Inténtalo otra vez o pregunta a un administrador.',
    'account.sso.domain': 'Esa cuenta no está entre las que pueden entrar aquí.',
    'account.sso.no_account': 'Todavía no hay cuenta aquí para ese correo. Pide a un administrador que te añada y vuelve a intentarlo.',
    'account.sso.identity_conflict': 'Otra cuenta del servicio de inicio de sesión ya está vinculada a esta persona. Pregunta a un administrador.',
    'account.sso.other': 'No se ha podido entrar. Inténtalo otra vez.',

    'account.callback.title': 'Entrando…',
    'account.callback.wait': 'Comprobando el enlace…',
    'account.callback.incomplete': 'A este enlace le falta una parte.',
    'account.callback.invalid': 'El enlace no es válido, ya se ha usado o ha caducado.',
    'account.callback.failed': 'Este enlace no funciona',
    'account.callback.failedHint': 'Cada enlace vale una vez y caduca a los 15 minutos. Pide otro: tarda un momento.',
    'account.callback.back': 'Pedir un enlace nuevo',
  },
  en: {
    'account.title': 'Your account',

    'account.invitations.title': { one: 'You have been invited to a brand', other: 'You have been invited to {count} brands' },
    'account.invitations.hint': 'Nothing changes until you answer. If you accept, you join the brand with the role they offer.',
    'account.invitations.as': 'as {role}',
    'account.invitations.by': 'from {who}',
    'account.invitations.expires': 'expires {date}',
    'account.invitations.accept': 'Accept',
    'account.invitations.decline': 'Decline',
    'account.invitations.accepted': 'You are now in {brand}',
    'account.invitations.declined': 'Invitation declined',
    'account.invitations.unknown': 'If you do not know who is inviting you, decline it.',

    'account.profile.title': 'Profile',
    'account.profile.brands': { one: 'Your brand', other: 'Your {count} brands' },
    'account.profile.paused': 'Paused',
    'account.profile.nameHint': 'Your name and email are set by an admin when they add you to a brand.',
    'account.profile.signOut': 'Sign out',

    'account.language.title': 'Language',
    'account.language.hint': "The interface's, just for you. It does not change the language the brand publishes in.",
    'account.language.note': 'Kept in this browser.',

    'account.twofa.title': 'Authenticator app',
    'account.twofa.hint': 'A code from an app on your phone, on top of the email link, to sign in.',
    'account.twofa.on': 'On',
    'account.twofa.off': 'Off',
    'account.twofa.required': 'Your role needs a second factor, so it cannot be turned off. If you lose your phone, an admin can reset it.',
    'account.twofa.intro': 'Adds a code from an app on your phone to signing in. Admins and approvers need one; for everyone else it is optional.',
    'account.twofa.introShort': 'Admins and approvers need one; for everyone else it is optional.',
    'account.twofa.setUp': 'Authenticator set up',
    'account.twofa.codesLeft': { one: 'You have {count} recovery code left.', other: 'You have {count} recovery codes left.' },
    'account.twofa.codesLow': 'Make new ones before they run out.',
    'account.twofa.newCodes': 'Make new recovery codes',
    'account.twofa.newCodesHint': 'The old ones stop working.',
    'account.twofa.turnOff': 'Turn off',
    'account.twofa.turnOffHint': 'You will be asked for no code when you sign in.',
    'account.twofa.removed': 'Authenticator removed',

    'account.code.label': 'Code from your app',

    'account.recovery.title': 'Keep these recovery codes',
    'account.recovery.hint': 'If you lose your phone, each of these lets you sign in once. They are shown now and never again.',
    'account.recovery.copyAll': 'Copy all',
    'account.recovery.saved': 'I have put them somewhere safe',
    'account.recovery.done': 'I have saved them',
    'account.recovery.fileTitle': 'Postbay recovery codes',
    'account.recovery.fileNote': 'Each works once.',
    'account.recovery.fileName': 'postbay-recovery-codes.txt',

    'account.enroll.intro': 'An authenticator app (Google Authenticator, Microsoft Authenticator, 1Password, Authy…) gives you a new six-digit code every 30 seconds.',
    'account.enroll.start': 'Set up an authenticator',
    'account.enroll.step1': 'In your authenticator app, add an account and choose “enter a setup key”, then type this key (spaces do not matter):',
    'account.enroll.copyKey': 'Copy key',
    'account.enroll.openApp': 'Open in an app on this device',
    'account.enroll.step2': 'Type the six-digit code the app shows now.',
    'account.enroll.codeLabel': 'Code from the app',
    'account.enroll.check': 'Check the code',
    'account.enroll.continue': 'Continue',

    'account.verify.codeLabel': 'Code from your authenticator app',
    'account.verify.recoveryLabel': 'Recovery code',
    'account.verify.recoveryHint': 'One of the ten you were given. Each works once.',
    'account.verify.continue': 'Continue',
    'account.verify.useApp': 'Use the app instead',
    'account.verify.useRecovery': 'I lost my phone: use a recovery code',

    'account.second.verifyTitle': 'One more step',
    'account.second.verifyHint': 'Type the code your authenticator app shows now.',
    'account.second.enrollTitle': 'Set up your second factor',
    'account.second.enrollHint': 'Your role needs one, so that a stolen sign-in link is not enough to approve or publish.',
    'account.second.notYou': 'Not you?',
    'account.second.signOut': 'Sign out',

    'account.notify.title': 'Notifications',
    'account.notify.hint': 'The bell always shows everything. Choose what also comes to you by email, and by push to this and your other browsers.',
    'account.notify.when': 'Tell me when…',
    'account.notify.email': 'Email',
    'account.notify.push': 'Push',
    'account.notify.emailFor': 'Email: {what}',
    'account.notify.pushFor': 'Push: {what}',

    'account.push.here': 'Push in this browser',
    'account.push.on': 'On',
    'account.push.off': 'Off',
    'account.push.unsupportedChip': 'Not available',
    'account.push.onHint': 'They reach you even with the tab closed.',
    'account.push.offHint': 'Turn it on to hear about things without Postbay open.',
    'account.push.unsupported': 'This browser cannot receive push messages.',
    'account.push.devices': { one: 'You have push on in {count} browser.', other: 'You have push on in {count} browsers.' },
    'account.push.turnOn': 'Turn on here',
    'account.push.turnOff': 'Turn off here',
    'account.push.test': 'Send a test',
    'account.push.onToast': 'Push is on in this browser',
    'account.push.offToast': 'Push is off in this browser',
    'account.push.sentTo': { one: 'Sent to {count} browser', other: 'Sent to {count} browsers' },
    'account.push.notTaken': 'The push service did not take it: try again in a moment',
    'account.push.noBrowser': 'No browser has push turned on',
    'account.push.blocked': "Notifications are blocked for this site: allow them in the browser's site settings, then try again",

    'account.kind.version.uploaded': 'A new version is ready for review',
    'account.kind.comment.created': 'Someone comments on a piece',
    'account.kind.version.changes_requested': 'Changes are requested on a version',
    'account.kind.version.approved': 'A version is approved',
    'account.kind.publication.due': 'A publication you publish by hand is due',
    'account.kind.publication.reapproval': 'A change to something scheduled needs confirming',
    'account.kind.publication.on_hold': 'Scheduled publications are put on hold',
    'account.kind.publication.published': 'A post goes out',
    'account.kind.publication.failed': 'A post fails to publish',
    'account.kind.publication.private': 'A post is uploaded but private',
    'account.kind.account.reconnect': 'An account has to be connected again',
    'account.kind.account.expiring': 'An account connection is about to expire',
    'account.kind.webhook.failing': 'A webhook is failing',
    'account.kind.slack.failing': 'Slack stops taking messages',
    'account.kind.agent.needs_person': 'The agent hands a piece back to a person',
    'account.kind.agent.failed': 'An agent run fails',

    'account.login.title': 'Sign in to Postbay',
    'account.login.subtitle': 'We email you a link: there is no password to remember.',
    'account.login.subtitleNoEmail': "Sign in with your team's account.",
    'account.login.email': 'Email',
    'account.login.emailPlaceholder': 'you@company.com',
    'account.login.send': 'Email me a link',
    'account.login.sending': 'Sending…',
    'account.login.sso': 'Sign in with {provider}',
    'account.login.or': 'or',
    'account.login.dev': 'Development sign-in (no email)',
    'account.login.devHint': 'Only on this development server: signs in straight away with the email typed.',
    'account.login.noAccess': 'No access yet? Ask an admin of your brand to add you.',
    'account.login.sentTitle': 'Check your email',
    'account.login.sent': 'If {email} belongs to someone in a brand, a sign-in link is on its way. It works once and expires in 15 minutes.',
    'account.login.otherEmail': 'Use another email',

    'account.sso.cancelled': 'The sign-in was cancelled.',
    'account.sso.state': 'That sign-in did not start in this browser, or took too long. Start again.',
    'account.sso.provider': 'The sign-in service could not be reached, or is not set up properly. Try again, or ask an admin.',
    'account.sso.token': 'The sign-in service gave an answer this app could not accept. Try again, or ask an admin.',
    'account.sso.claims': 'The sign-in service gave an answer this app could not accept. Try again, or ask an admin.',
    'account.sso.domain': 'That account is not one of the accounts allowed to sign in here.',
    'account.sso.no_account': 'There is no account here for that email yet. Ask an admin to add you, then try again.',
    'account.sso.identity_conflict': 'Another account at the sign-in service is already linked to this person. Ask an admin.',
    'account.sso.other': 'The sign-in did not work. Try again.',

    'account.callback.title': 'Signing you in…',
    'account.callback.wait': 'Checking the link…',
    'account.callback.incomplete': 'This link is incomplete.',
    'account.callback.invalid': 'That link is invalid, already used or expired.',
    'account.callback.failed': 'This link does not work',
    'account.callback.failedHint': 'Each link works once and expires after 15 minutes. Ask for another: it only takes a moment.',
    'account.callback.back': 'Ask for a new link',
  },
});
